/**
 * Manufacturing-catalog lookups + the `RfqLineSpecification` cascade validator
 * (UID-346, ADR-0117 D4).
 *
 * This module is the CLOSED-SET GUARANTEE the contract deliberately does not get
 * from an enum. ADR-0117 D4 declines to mint `PartInspectionKind` /
 * `PartCertificateKind` on the grounds that a flat enum cannot express the
 * actual invariant — certificates and inspections are gated PER MATERIAL, and
 * certificates are forbidden outright for `urethane_casting` — so a cascade
 * validator has to exist either way, and once it does the enum's marginal value
 * is small against a recurring churn cost (the option data regenerates from the
 * ERP export; a published enum would have to be re-cut, re-composed and
 * re-published each time). This file is that validator.
 *
 * Pure and framework-free, in `packages/shared` so BOTH the route edge
 * (`apps/api`) and the modal (`apps/web`) validate against the same rules — the
 * server is the trust boundary (D5), the client only gets a better UX.
 *
 * IDENTITY CONVENTION: options are matched on catalog `name`, not `id`. That is
 * the convention the parts vertical already set for the identical modal
 * (`PartsBulkConfigureModal.tsx` selects with `m.name === material`) and what the
 * `RfqLineSpecification` contract descriptions carry as examples ("6061 Aluminum
 * (T6 / T651 / T6511)", "Anodize per Mil-PRF-8625 Type II"). Certificates and
 * inspections have no id in the catalog — they ARE names — so name-matching is
 * the only convention that treats all five fields alike. Matching is EXACT
 * (case- and whitespace-sensitive after trimming): a near-miss is a rejection,
 * never a silent coercion onto a different material.
 */
import { DEFAULT_FINISH_ADDER, FINISH_ADDERS } from './finish-rate-card'
import { MATERIALS_BY_PROCESS, PROCESS_GROUPS } from './manufacturing-catalog'
import type { FinishOption, ProcessMaterialOption } from './manufacturing-catalog'
import { PART_PROCESSES, type PartProcess, isPartProcess } from './part-process'
import type { Price } from './price'

/**
 * A per-field rejection. Mirrors the `errors` extension member the parts
 * vertical's 400 Problem+JSON already carries (`edit-part-config-policy.ts`'s
 * `ConfigFieldError`), so the modal can mark the offending control instead of
 * showing a bare toast.
 */
export interface SpecificationFieldError {
  readonly field: string
  readonly detail: string
}

/**
 * The typed per-line manufacturing specification — the server-side mirror of the
 * contract's `RfqLineSpecification` (`apps/api/openapi.yaml`). Hand-typed here
 * rather than generated, following the established repo pattern (there is no
 * backend contract-codegen; `contract.test.ts` asserts against the raw YAML).
 *
 * `finish` is REQUIRED-BUT-NULLABLE and that is load-bearing (D4): `null` means
 * "as machined / standard, no secondary operation" — a real choice, and the only
 * valid one for the catalog materials that offer no finish — which a plain
 * optional field could not distinguish from an unanswered question.
 *
 * NO `quantity` FIELD, on purpose. Quantity is a transient PRICING input, not
 * configuration (DM§35.2 refuses "a second, non-transactional home for a pricing
 * input"), so it rides `ConfigurationEstimateRequest` / `RfqBulkConfigureRequest`
 * instead. The line's own quantity stays the single quantity atom.
 */
export interface RfqLineSpecification {
  readonly process: PartProcess
  readonly material: string
  readonly finish: string | null
  readonly inspections?: readonly string[]
  readonly certificates?: readonly string[]
  /**
   * "I need an inspection you do not list; route me to a human" (ADR-0117 D4.1).
   *
   * A ROUTING STATE, not a fourth member of the inspection vocabulary — which is
   * exactly the confusion D4-as-written made and D4.1 corrected. It is therefore
   * a typed boolean rather than a sentinel string in {@link inspections}: ALWAYS
   * valid (there is no catalog lookup to do) and ALWAYS
   * `manual_quote_required`. It COMPOSES with listed inspections ("run your
   * Dimensional Inspection Report *and* this other thing"), and it does not
   * launder an invalid `inspections` member — an out-of-catalog NAME stays a 400,
   * because "the buyer wants something bespoke" and "the client sent a name that
   * does not exist" are different facts with different owners.
   *
   * Absent means false. No `default` in the contract, deliberately (see the
   * schema comment): openapi-typescript renders a defaulted property as required.
   */
  readonly customInspection?: boolean
}

const PROCESS_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  [...PROCESS_GROUPS.instantManual, ...PROCESS_GROUPS.manualQuote].map((entry) => [
    entry.id,
    entry.label,
  ]),
)

/** Display label for a process enum token, for use in an error `detail`. */
function processLabel(process: PartProcess): string {
  return PROCESS_LABELS[process] ?? process
}

/** The materials the catalog offers for `process`; empty for an unknown process. */
export function materialsFor(process: PartProcess): readonly ProcessMaterialOption[] {
  return MATERIALS_BY_PROCESS[process] ?? []
}

/** Lowercase, split on any run of non-alphanumerics, drop empties → token set. */
function materialTokens(raw: string): ReadonlySet<string> {
  return new Set(
    raw
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length > 0),
  )
}

/**
 * UID-553 — best-effort map of a FREE-TEXT material string (a customer's CSV
 * BOM cell, e.g. `"Aluminum 6061"` or `"6061"`) onto the catalog-EXACT material
 * name the typed config/spec requires for `process` (e.g.
 * `"6061 Aluminum (T6 / T651 / T6511)"`). Needed because the CSV authors free
 * text but {@link validateSpecification}/`parseSpecification` match a material
 * by exact, trim-only, case-sensitive equality — so a raw CSV cell almost never
 * matches without this mapping.
 *
 * DELIBERATELY CONSERVATIVE (UID-553 D6): tokenizes both sides (lowercase,
 * split on non-alphanumerics) and accepts a catalog option ONLY when the CSV
 * tokens are a SUBSET of that option's tokens AND exactly ONE option qualifies.
 * Zero matches (unknown material) or more than one (ambiguous — e.g. bare
 * `"aluminum"` matching several 6061/7075/… entries) both return `null`, so the
 * caller leaves the material blank rather than guessing a wrong material that
 * would misquote the part. An exact (trim-only) hit short-circuits first so a
 * CSV that already carries the catalog string is honored verbatim.
 *
 * Pure and driver-free — the single source of truth for CSV→catalog material
 * resolution, reused wherever a free-text material must become a typed one.
 */
export function resolveCatalogMaterial(
  process: PartProcess,
  rawText: string | null | undefined,
): string | null {
  if (typeof rawText !== 'string' || rawText.trim().length === 0) {
    return null
  }
  const options = materialsFor(process)
  if (options.length === 0) {
    return null
  }
  const trimmed = rawText.trim()
  // Exact (trim-only) match — the same equality the validator uses; honor a
  // CSV that already speaks the catalog's language before any fuzzy work.
  const exact = options.find((option) => option.name === trimmed)
  if (exact) {
    return exact.name
  }
  const wanted = materialTokens(trimmed)
  if (wanted.size === 0) {
    return null
  }
  const candidates = options.filter((option) => {
    const optionTokens = materialTokens(option.name)
    for (const token of wanted) {
      if (!optionTokens.has(token)) {
        return false
      }
    }
    return true
  })
  // Exactly one option's tokens supersets the CSV tokens → confident match.
  // Anything else is ambiguous or absent → leave blank (D6).
  return candidates.length === 1 ? (candidates[0]?.name ?? null) : null
}

/**
 * One material family/category cluster — the design's grouped material
 * dropdown (a `{{ m.isHeader }}` label row followed by its options,
 * `Fictiv Platform.dc.html:5471`). `category` is the ERP
 * `quote_material_category_name` already carried on every
 * `ProcessMaterialOption`; nothing else needs to be looked up.
 */
export interface MaterialGroup {
  /** The category label shown as the group header (e.g. "Aluminum"). */
  readonly category: string
  readonly materials: readonly ProcessMaterialOption[]
}

/**
 * The category used for options whose ERP `category` is null. A real subset of
 * the catalog (22 options today) carries no category on the export; they get
 * one honest trailing bucket rather than a fabricated family or silent drop.
 */
export const UNCATEGORIZED_MATERIAL_GROUP = 'Other'

/**
 * The materials for `process`, bucketed by `category` for the design's grouped
 * dropdown. Groups appear in FIRST-SEEN order (the catalog's own per-process
 * ordering, which the ERP export already curates), so this adds grouping
 * without reordering within a family. Null-category options collapse into a
 * single trailing {@link UNCATEGORIZED_MATERIAL_GROUP} bucket. Empty for an
 * unknown process.
 *
 * Owned here (not in the modal) so the parts vertical's Edit-config modal and
 * the RFQ vertical's identical material combobox share one grouping rule —
 * same single-owner reasoning as `materialsFor`/`certificatesFor` above.
 */
export function materialsGroupedByCategory(process: PartProcess): readonly MaterialGroup[] {
  const groups: MaterialGroup[] = []
  const byCategory = new Map<string, ProcessMaterialOption[]>()
  for (const material of materialsFor(process)) {
    const category = material.category ?? UNCATEGORIZED_MATERIAL_GROUP
    let bucket = byCategory.get(category)
    if (bucket === undefined) {
      bucket = []
      byCategory.set(category, bucket)
      // Placeholder preserves first-seen order; `materials` is the SAME array
      // reference `bucket` fills below, so no second pass is needed.
      groups.push({ category, materials: bucket })
    }
    bucket.push(material)
  }
  // The "Other" bucket, if present, sorts LAST — a real-but-uncategorized
  // grab-bag reads better after the named families than interleaved by
  // whenever its first member happened to appear.
  return groups.sort((a, b) => {
    if (a.category === UNCATEGORIZED_MATERIAL_GROUP) return 1
    if (b.category === UNCATEGORIZED_MATERIAL_GROUP) return -1
    return 0
  })
}

function materialOption(process: PartProcess, material: string): ProcessMaterialOption | undefined {
  const wanted = material.trim()
  if (wanted.length === 0) {
    return undefined
  }
  return materialsFor(process).find((option) => option.name === wanted)
}

/**
 * The secondary finishes offered for `material` under `process`. EMPTY is a
 * meaningful answer, not an error: 26 catalog materials offer no secondary
 * finish, for which `finish: null` ("as machined / standard") is the only valid
 * specification. Also empty for an unknown material — callers get the material
 * error from {@link validateSpecification}, not a throw.
 */
export function finishesFor(process: PartProcess, material: string): readonly FinishOption[] {
  return materialOption(process, material)?.finishes ?? []
}

/**
 * The whole-dollar price adder a finish contributes to a configuration estimate
 * (ADR-0117 D8) — the SINGLE definition of that lookup.
 *
 * `null` — "as machined / standard, no secondary operation" — adds NOTHING,
 * matching the design prototype's `f.id === '__std' → 0`. A finish the rate card
 * does not name falls to {@link DEFAULT_FINISH_ADDER}.
 *
 * Owned here, next to {@link finishesFor}, because two surfaces need the same
 * number: `MockConfigurationPricingPort` CHARGES it inside the estimate, and the
 * Part Configurator's finish tiles DISPLAY it per option (UID-503). A second copy
 * would let the tile advertise a delta the estimate does not charge — the same
 * drift failure D4.2 already had to fix once on the manual-quote rule.
 *
 * Matching is EXACT, deliberately: no trim, no case folding. Canonicalization is
 * {@link parseSpecification}'s job and its output is what the pricer looks up, so
 * re-deriving it here would be the second copy of a rule this function exists to
 * avoid — and a near-match on a rate is a wrong price, not a helpful one.
 */
export function finishPriceAdderFor(finish: string | null): number {
  if (finish === null) {
    return 0
  }
  return FINISH_ADDERS[finish] ?? DEFAULT_FINISH_ADDER
}

/**
 * UID-507 — shifts `price` by the DIFFERENCE between two finishes' own $
 * adders (`finishPriceAdderFor`), never a full re-price. `previousFinish` is
 * whatever finish `price` already reflects; `nextFinish` is the one now
 * chosen. Both are wire values (`null` = "as machined / standard").
 *
 * THE ONE PLACE THIS MATH HAPPENS, for the same D4.2 "two copies can
 * disagree" reason `finishPriceAdderFor` itself documents: the Part
 * Configurator's pricing footer (client-side display delta, UID-505) and the
 * Library's `PATCH /parts/{partId}` handler (server-side persisted delta,
 * UID-507 — so a reorder/add-to-cart replays the SAME number the footer
 * showed, not the pre-finish base) both need this identical delta, and a
 * second hand-rolled copy of it is exactly how UID-505's regression happened
 * in the first place (a live, DIFFERENT pricing model quietly replacing this
 * one).
 *
 * Stays a plain decimal-string amount (never a float multiply), mirroring
 * every other money value in this codebase.
 */
export function priceWithFinishDelta(
  price: Price,
  previousFinish: string | null,
  nextFinish: string | null,
): Price {
  const amount = Number.parseFloat(price.amount)
  if (!Number.isFinite(amount)) {
    return price
  }
  const delta = finishPriceAdderFor(nextFinish) - finishPriceAdderFor(previousFinish)
  if (delta === 0) {
    return price
  }
  return { ...price, amount: (amount + delta).toFixed(2) }
}

/**
 * The certificates offered for `material` under `process`. Certificates are
 * GATED ON MATERIAL — the modal cannot offer any until a material is chosen —
 * and always empty when {@link certificatesAllowedFor} is false.
 */
export function certificatesFor(process: PartProcess, material: string): readonly string[] {
  return materialOption(process, material)?.certificates ?? []
}

/** The inspections offered for `material` under `process`. Per-material, from the real ERP set. */
export function inspectionsFor(process: PartProcess, material: string): readonly string[] {
  return materialOption(process, material)?.inspections ?? []
}

/**
 * Whether `process` permits certificates at all. Derived from the catalog's own
 * per-material `certificatesAllowed` flag (itself derived from the real
 * `CERTIFICATION_ALLOWED_PROCESS_TYPES`), so this tracks the ERP export rather
 * than hard-coding the one process that is false today (`urethane_casting`).
 * An unknown process yields false — nothing is permitted for a process that
 * does not exist.
 */
export function certificatesAllowedFor(process: PartProcess): boolean {
  const materials = materialsFor(process)
  return materials.length > 0 && materials.every((option) => option.certificatesAllowed)
}

/**
 * Whether the CHOSEN MATERIAL permits certificates — the per-material gate, and
 * the SINGLE definition of it.
 *
 * This exists because the rule was being re-derived inline
 * (`selectedMaterialOption?.certificatesAllowed ?? true`) and the two copies
 * DISAGREED on the unknown case: permissive there, restrictive here. A
 * disagreement on a gate means the UI offers a control the server then 400s.
 *
 * FAIL-CLOSED, deliberately. An unknown or unchosen material yields `false`,
 * because certificates are gated on material: until the buyer has chosen one
 * there is no catalog entry to permit anything, and `certificatesFor` returns
 * `[]` in exactly that case anyway — so `false` is not a restriction, it is the
 * honest statement that nothing is offerable yet. The failure directions are not
 * symmetric: fail-closed hides a control that would be rejected, fail-open
 * offers one and then rejects the submission.
 *
 * Use {@link certificatesAllowedFor} only for the PROCESS-level rollup (the "is
 * this process certificate-bearing at all" question). Anything gating a control
 * or validating a request wants this one.
 */
export function materialCertificatesAllowed(process: PartProcess, material: string): boolean {
  return materialOption(process, material)?.certificatesAllowed ?? false
}

function firstNonMember(
  requested: readonly string[],
  allowed: readonly string[],
): string | undefined {
  const permitted = new Set(allowed)
  return requested.map((value) => value.trim()).find((value) => !permitted.has(value))
}

function asStringArray(
  value: unknown,
  field: string,
  errors: SpecificationFieldError[],
): readonly string[] {
  if (value === undefined || value === null) {
    return []
  }
  if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) {
    errors.push({ field, detail: `${field} must be an array of strings` })
    return []
  }
  return value as readonly string[]
}

/**
 * The outcome of {@link parseSpecification}: either the CANONICAL specification,
 * or the per-field reasons it is not one. Never both — a specification that did
 * not validate has no canonical form to speak of.
 */
export type SpecificationParseResult =
  | {
      readonly ok: true
      readonly specification: RfqLineSpecification
      readonly errors: readonly []
    }
  | { readonly ok: false; readonly errors: readonly SpecificationFieldError[] }

const NO_ERRORS: readonly [] = []

/**
 * Validate a specification for MEMBERSHIP and CASCADE COHERENCE against the
 * shared catalog AND return its canonical form. Never throws, so a route handler
 * can map `errors` straight onto a 400's `errors` member.
 *
 * WHY THIS RETURNS THE SPECIFICATION, and why it is the only place that trims.
 * Matching has always been trim-insensitive here (`"  6061 Aluminium "` is
 * accepted), but the CALLERS were not: the pricer looked the material up with
 * `entry.name === material` on the RAW string, so a whitespace-padded name
 * cleared validation, missed the rate table, and silently priced at the DEFAULT
 * category tier — a wrong price from a client-controlled string — and then
 * persisted in non-canonical form, so two rows meaning the same thing did not
 * compare equal. The fix is ONE canonicalizing seam whose output every downstream
 * consumer uses, rather than a second trim in the pricer: a second copy of a rule
 * is exactly what produced the other live bug in this ticket (ADR-0117 D4.2).
 *
 * Canonicalization is deliberately CONSERVATIVE — trim only. No case folding, no
 * unicode normalization, no fuzzy matching: a near-miss on a material is a
 * rejection, never a silent coercion onto a different (differently-priced) one.
 * Key SHAPE is preserved, because shape carries meaning here: `finish: null`
 * survives as the explicit "as machined" choice, an absent `inspections` stays
 * absent rather than becoming `[]` ("not asked" ≠ "asked for nothing"), and
 * `customInspection: false` collapses to absence, since false is silence.
 *
 * Takes `unknown` deliberately: this runs at the TRUST BOUNDARY on a body that
 * has cleared ajv but is still client-supplied, and it is also the guard behind
 * which legacy persisted rows are read (D5). It re-checks shape rather than
 * assuming the declared type.
 *
 * NO DOWNSTREAM CASCADE OFF A BROKEN PARENT. An invalid `process` reports only
 * `process`, and an invalid `material` reports only `material` — the dependent
 * fields are not additionally flagged, because the modal would otherwise paint
 * errors on controls whose values may well be fine once the parent is fixed, and
 * the buyer cannot act on four errors caused by one wrong select.
 */
export function parseSpecification(input: unknown): SpecificationParseResult {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return {
      ok: false,
      errors: [{ field: 'specification', detail: 'specification must be an object' }],
    }
  }

  const errors: SpecificationFieldError[] = []
  const spec = input as Record<string, unknown>

  const { process } = spec
  if (!isPartProcess(process)) {
    return {
      ok: false,
      errors: [
        {
          field: 'process',
          detail: `process must be one of ${PART_PROCESSES.join(', ')}`,
        },
      ],
    }
  }

  const { material } = spec
  if (typeof material !== 'string' || material.trim().length === 0) {
    return {
      ok: false,
      errors: [
        { field: 'material', detail: 'material is required and must be a non-empty string' },
      ],
    }
  }
  const option = materialOption(process, material)
  if (option === undefined) {
    return {
      ok: false,
      errors: [
        {
          field: 'material',
          detail: `"${material}" is not a material offered for ${processLabel(process)}`,
        },
      ],
    }
  }

  // `finish` is a REQUIRED KEY with a nullable value (D4) — an absent key is an
  // unanswered question, which is exactly what this ticket exists to reject.
  if (!('finish' in spec)) {
    errors.push({
      field: 'finish',
      detail: 'finish is required; use null for "as machined / standard, no secondary finish"',
    })
  } else {
    const { finish } = spec
    if (finish !== null) {
      if (typeof finish !== 'string' || finish.trim().length === 0) {
        errors.push({
          field: 'finish',
          detail: 'finish must be a non-empty string, or null for "as machined / standard"',
        })
      } else if (!option.finishes.some((entry) => entry.name === finish.trim())) {
        errors.push({
          field: 'finish',
          detail: `"${finish}" is not a finish offered for ${option.name}${
            option.finishes.length === 0
              ? ' — that material has no secondary finish, so finish must be null'
              : ''
          }`,
        })
      }
    }
  }

  const inspections = asStringArray(spec.inspections, 'inspections', errors)
  const unknownInspection = firstNonMember(inspections, option.inspections)
  if (unknownInspection !== undefined) {
    errors.push({
      field: 'inspections',
      detail: `"${unknownInspection}" is not an inspection offered for ${option.name}`,
    })
  }

  // The escape hatch (D4.1). NOT a membership check and not related to
  // `inspections` — the only thing to validate is that it is a boolean. The
  // schema's `additionalProperties: false` means the field must be admitted
  // HERE too, or ajv rejects the body before this validator ever runs.
  const { customInspection } = spec
  if (customInspection !== undefined && typeof customInspection !== 'boolean') {
    errors.push({ field: 'customInspection', detail: 'customInspection must be a boolean' })
  }

  const certificates = asStringArray(spec.certificates, 'certificates', errors)
  if (certificates.length > 0 && !option.certificatesAllowed) {
    errors.push({
      field: 'certificates',
      detail: `certificates are not available for ${processLabel(process)}`,
    })
  } else {
    const unknownCertificate = firstNonMember(certificates, option.certificates)
    if (unknownCertificate !== undefined) {
      errors.push({
        field: 'certificates',
        detail: `"${unknownCertificate}" is not a certificate offered for ${option.name}`,
      })
    }
  }

  if (errors.length > 0) {
    return { ok: false, errors }
  }

  // ── The canonical form ────────────────────────────────────────────────────
  // Built ONLY on the success path, from values already proven to be catalog
  // members. `option.name` is used for `material` rather than the client's
  // trimmed string so the persisted value is the CATALOG's spelling by
  // construction, not a string that merely compared equal to it.
  const finish = spec.finish
  const canonical: {
    process: PartProcess
    material: string
    finish: string | null
    inspections?: readonly string[]
    certificates?: readonly string[]
    customInspection?: boolean
  } = {
    process,
    material: option.name,
    finish: typeof finish === 'string' ? finish.trim() : null,
  }
  if (isPresent(spec.inspections)) {
    canonical.inspections = trimAll(spec.inspections as readonly string[])
  }
  if (isPresent(spec.certificates)) {
    canonical.certificates = trimAll(spec.certificates as readonly string[])
  }
  if (spec.customInspection === true) {
    canonical.customInspection = true
  }
  return { ok: true, specification: canonical, errors: NO_ERRORS }
}

function isPresent(value: unknown): boolean {
  return value !== undefined && value !== null
}

/**
 * Trim, then DEDUPE — the canonical form of a set-valued field.
 *
 * Deduping belongs here, in the one canonicalizing seam, for two reasons. The
 * contract declares `uniqueItems: true` on both `inspections` and
 * `certificates`, so a duplicate is not a representable value on the wire; and
 * trimming is what MAKES a near-duplicate an exact one, so the two steps cannot
 * be separated without leaving `[' X ', 'X']` to slip through.
 *
 * UID-530 (Gate-5 LOW-1) made this load-bearing rather than belt-and-braces:
 * the library-configuration path validates through `parseSpecification` WITHOUT
 * passing ajv first (the values come from `library.part.config_attributes`, whose
 * own validator checks lengths only), so before this it was possible to persist
 * duplicates and then serve them on a response declaring uniqueness. Harmless
 * while no per-item price adder exists — a double-charge the day one lands.
 *
 * Order is preserved (first occurrence wins), so the canonical form stays a
 * stable, comparable value rather than depending on set iteration order.
 */
function trimAll(values: readonly string[]): readonly string[] {
  return [...new Set(values.map((value) => value.trim()))]
}

/**
 * {@link parseSpecification}'s verdict alone, for the callers that only need to
 * know whether a specification is coherent — the create/update gate and the
 * read-side guard over legacy persisted rows, neither of which mints anything
 * from the canonical form.
 *
 * A THIN DELEGATE ON PURPOSE. There is one implementation of the cascade rules;
 * this is a projection of it, not a second pass, so the two can never disagree
 * about whether a specification is valid.
 */
export function validateSpecification(input: unknown): readonly SpecificationFieldError[] {
  return parseSpecification(input).errors
}
